import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "../../api";
import { formatDateTime } from "../../../shared/time";
import type { HomePayload } from "../../../shared/types";
import { Badge } from "../../ui";

export function HomePage() {
  const [home, setHome] = useState<HomePayload | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { void api<HomePayload>("/api/v1/home").then(setHome).catch((caught: Error) => setError(caught.message)); }, []);
  if (error) return <section className="page"><p className="error-banner">{error}</p></section>;
  if (!home) return <section className="page">Loading the room…</section>;
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Moksha Base</p>
          <h1 className="page-title">{home.greeting}</h1>
          <p className="lede">The next gathering, the people still deciding, and the work sitting on the table.</p>
        </div>
        <div className="cluster">
          <Link className="btn primary" to="/services/plan_sunday">Open Sunday</Link>
          <Link className="btn teal" to="/stand/plan_sunday">Music Stand</Link>
        </div>
      </header>
      <div className="grid-3">
        <article className="panel panel-pad"><p className="faint">Songs in the library</p><strong className="page-title" style={{ fontSize: 36 }}>{home.counts.songs}</strong></article>
        <article className="panel panel-pad"><p className="faint">Pending responses</p><strong className="page-title" style={{ fontSize: 36 }}>{home.counts.pendingResponses}</strong></article>
        <article className="panel panel-pad"><p className="faint">Open tasks</p><strong className="page-title" style={{ fontSize: 36 }}>{home.counts.openTasks}</strong></article>
      </div>
      <div className="grid-2">
        <article className="panel panel-pad">
          <h2>Coming up</h2>
          {home.upcomingPlans.map((plan) => (
            <Link className="list-row" key={plan.id} to={`/services/${plan.id}`}>
              <span className="time">{formatDateTime(plan.startsAt)}</span>
              <span><strong>{plan.title}</strong><div className="muted">{plan.serviceTypeName} · {plan.itemCount} items</div></span>
              {plan.pendingResponses > 0 ? <Badge tone="amber">{plan.pendingResponses} waiting</Badge> : <Badge tone="teal">{plan.status}</Badge>}
            </Link>
          ))}
        </article>
        <div className="stack">
          <article className="panel panel-pad">
            <h2>Your assignments</h2>
            {home.myAssignments.length === 0 && <p className="muted">Nothing scheduled under this login.</p>}
            {home.myAssignments.map((item) => (
              <Link key={item.id} className="list-row" to={`/services/${item.planId}`} style={{ gridTemplateColumns: "1fr auto" }}>
                <span><strong>{item.position}</strong><div className="muted">{item.planTitle}</div></span>
                <Badge tone={item.status === "accepted" ? "teal" : item.status === "declined" ? "danger" : "amber"}>{item.status}</Badge>
              </Link>
            ))}
          </article>
          <article className="panel panel-pad">
            <h2>Tasks</h2>
            {home.tasks.map((task) => <div key={task.id} className="list-row" style={{ gridTemplateColumns: "1fr auto" }}><span>{task.title}<div className="faint">{task.assigneeName} · {task.dueOn}</div></span><Link to="/tasks">Open</Link></div>)}
            {home.givingThisMonthCents !== null && <p className="muted">October ledger {money(home.givingThisMonthCents)}. Card payments are not configured.</p>}
          </article>
          <article className="panel panel-pad">
            <h2>Recent changes</h2>
            {home.activity.map((item) => <p key={item.id} className="muted">{item.summary}</p>)}
          </article>
        </div>
      </div>
    </section>
  );
}

export function SearchPage() {
  const params = new URLSearchParams(window.location.search);
  const query = params.get("q") ?? "";
  const [results, setResults] = useState<{ songs: Array<{ id: string; label: string }>; people: Array<{ id: string; label: string }>; plans: Array<{ id: string; label: string }>; events: Array<{ id: string; label: string }> } | null>(null);
  useEffect(() => { if (query) void api<NonNullable<typeof results>>(`/api/v1/search?q=${encodeURIComponent(query)}`).then(setResults); }, [query]);
  return (
    <section className="page">
      <h1 className="page-title">Search</h1>
      <p className="lede">Results for “{query}”.</p>
      {results && (
        <div className="grid-2">
          <List title="Songs" items={results.songs.map((item) => ({ ...item, href: `/songs/${item.id}` }))} />
          <List title="People" items={results.people.map((item) => ({ ...item, href: `/people/${item.id}` }))} />
          <List title="Plans" items={results.plans.map((item) => ({ ...item, href: `/services/${item.id}` }))} />
          <List title="Events" items={results.events.map((item) => ({ ...item, href: `/calendar/${item.id}` }))} />
        </div>
      )}
    </section>
  );
}

function List({ title, items }: { title: string; items: Array<{ id: string; label: string; href: string }> }) {
  return <article className="panel panel-pad"><h2>{title}</h2>{items.length === 0 && <p className="muted">None</p>}{items.map((item) => <Link key={item.id} className="list-row" to={item.href} style={{ gridTemplateColumns: "1fr" }}>{item.label}</Link>)}</article>;
}
